import { ProductLicenseGuard } from '@auth/services/product-license-guard.service';
import { Routes } from "@angular/router";
import { GlobalNotFoundComponent } from "@containers/global-not-found/global-not-found.component";
import { ErrorPageComponent } from "@auth/containers/error-page/error-page.component";
import { UsersPageComponent } from "@users/containers/users-page/users-page.component";
import { RoleGuardService } from "@roles/services/role-guard.service";
import { NewUserPageComponent } from "@users/containers/new-user-page/new-user-page.component";
import { OidcAuthRouteGuardService } from "@auth/services/oidc-auth-route-guard";
import { SigninCallbackComponent } from "@auth/components/signin-callback/signin-callback.component";
import { InvalidPlatformComponent } from "@invalid-platform/invalid-platform.component";
import { NotLicensedComponent } from "@not-licensed/components/not-licensed.component";
import { DashboardHomePageRouteGuard } from "@common/routing/dashboard-homepage.guard";
import { ExternalUrlRouteGuard } from "@common/routing/external-url.guard";
import { DashboardRouteContext } from "@context/old_index";
import { ProductRouteNavigator } from "@product/services/product-route-navigator";
import { MojoWellKnownNames } from "@common/constants/mojo-well-known-names";
import { dashboardRoutes } from "./dashboard-routes";
import { dashboardDesignerRoutes } from "./dashboard-designer-routes";
import { administrationRoutes } from "./admin-routes";
import { listerRoutes } from "./lister-routes";
import {ClientAdmin, SysAdmin} from "./role-constants";
import { AppLayoutComponent } from "@feature/_app_modules/layout/app-layout/app-layout.component";
import { EmptyFaqLayoutComponent } from '@feature/_app_modules/layout/layouts/empty-faq-layout/empty-faq-layout/empty-faq-layout.component';
import { UserPreferencesComponent } from '@feature/user-preferences/components/user-preferences.component';
import {
  DownloadMftBulkUploadResultComponent
} from "@mft/user-admin/containers/mft-bulk-upload/download-mft-bulk-upload-result.component";
import {AboutPageComponent} from "@about/containers/about-page.component";
import {EntityDataLayoutComponent} from "@layout/layouts/entity-data/entity-data-layout.component";
import {entityDataRedirectRoutes} from "@routing/entity-data-redirects";
import {AddOnGuard} from "@auth/services/add-on-guard.service";
import {InvalidEntityComponent} from "@feature/invalid-entity/components/invalid-entity.component";

export function getRoutes(): Routes {
  return routes;
}

export const routes: Routes = [
  // mobile
  {
    path: "m",
    pathMatch: "prefix",
    canActivate: [OidcAuthRouteGuardService],
    children: [
      {
        path: "mft",
        pathMatch: "prefix",
        canActivate: [ ProductLicenseGuard ],
        data: {
          products: [MojoWellKnownNames.Products.MojoForTenants]
        },
        loadChildren: () =>
          import(
            "@feature/_app_modules/mojo-for-tenants-mobile/mojo-for-tenants-mobile.module"
          ).then((m) => m.MojoForTenantsMobileModule),
      },
      // 404 & Catch all for /m
      {path: '404-not-found', pathMatch: 'full', loadChildren: () => import('@feature/modules/admin/error/error-404/error-404.module').then(m => m.Error404Module)},
      {path: '**', redirectTo: '404-not-found'}
    ],
  },
  {
    path: "faq",
    pathMatch: "full",
    canActivate: [OidcAuthRouteGuardService],
    component: EmptyFaqLayoutComponent,
    loadChildren: () => import('@feature/_app_modules/responsive-faq/responsive-faq.module').then(m => m.ResponsiveFaqModule)
  },
  ...entityDataRedirectRoutes,
  {
    path: "entity-data",
    pathMatch: "prefix",
    canActivate: [OidcAuthRouteGuardService],
    component: EntityDataLayoutComponent,
    children: [
      ...listerRoutes,
      ...dashboardRoutes,
    ],
    data: {
      reuseRoute: true
    }
  },
  {
    path: "",
    pathMatch: "prefix",
    component: AppLayoutComponent,
    data: {
      layout: "original",
    },
    children: [
      {
        path: "mfs",
        pathMatch: "prefix",
        data: {
          addOn: MojoWellKnownNames.AddOnCodes.WS005_Mfs,
          product:  MojoWellKnownNames.Products.MojoForSuppliers,
        },
        canActivate: [AddOnGuard, OidcAuthRouteGuardService],
        children: [
          {
            path: "",
            loadChildren: () =>
              import(
                "@feature/_app_modules/mojo-for-suppliers/mojo-for-suppliers.module"
              )
                .then((m) => m.MojoForSuppliersModule),
          },
        ],
      },
      {
        path: "mft",
        pathMatch: "prefix",
        data: {
          addOn: MojoWellKnownNames.AddOnCodes.WS006_Mft,
          product:  MojoWellKnownNames.Products.MojoForTenants,
        },
        canActivate: [AddOnGuard, OidcAuthRouteGuardService],
        children: [
          {
            path: "",
            loadChildren: () =>
              import(
                "@feature/_app_modules/mojo-for-tenants/mojo-for-tenants.module"
              )
                .then((m) => m.MojoForTenantsModule),
          },
        ],
      },
      {
        path: "download-mft-bulk-upload-result",
        component: DownloadMftBulkUploadResultComponent,
        canActivate: [OidcAuthRouteGuardService, RoleGuardService],
        data: {
          roles: [SysAdmin],
        },
      },
      { path: "not-licensed", component: NotLicensedComponent },
      { path: "invalid-entity", component: InvalidEntityComponent },
      { path: "invalid-platform", component: InvalidPlatformComponent },
      { path: "signin-callback", component: SigninCallbackComponent },
      { path: "error", component: ErrorPageComponent },
      { path: "newuser", component: NewUserPageComponent },
      {
        path: "users",
        component: UsersPageComponent,
        canActivate: [OidcAuthRouteGuardService],
      },
      {
        path: 'user-preferences',
        pathMatch: 'full',
        component: UserPreferencesComponent,
        canActivate: [OidcAuthRouteGuardService],
      },
      {
        path: 'about',
        pathMatch: 'full',
        component: AboutPageComponent,
        canActivate: [OidcAuthRouteGuardService],
      },
      ...dashboardDesignerRoutes,
      ...administrationRoutes,
      {
        path: "",
        pathMatch: "full",
        canActivate: [OidcAuthRouteGuardService],
        children: [
          {
            path: "",
            canActivate: [ProductRouteNavigator],
            children: [
              {
                path: "",
                canActivate: [DashboardHomePageRouteGuard],
                children: [
                  {
                    path: "",
                    canActivate: [RoleGuardService],
                    loadChildren: () =>
                      import(
                        "@feature/_app_modules/mojo-for-clients/admin-dashboard/admin-dashboard.module"
                      )
                        .then((m) => m.AdminDashboardModule),
                    data: {
                      defaultRoute: "/main",
                      adminRoute: "/admin",
                      roles: [
                        ClientAdmin,
                      ],
                      routeContext: DashboardRouteContext.create("None", false),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "externalRedirect",
        canActivate: [ExternalUrlRouteGuard],
        component: GlobalNotFoundComponent,
      },
      { path: "404", component: GlobalNotFoundComponent },
      { path: "**", component: GlobalNotFoundComponent },
    ],
  },
];
